import React, { useState, useMemo, useRef } from 'react';
import { 
    Box, Heading, Text, HStack, VStack, InputGroup, Input, 
    InputRightElement, IconButton, Card, CardHeader, CardBody, 
    Icon, Select
} from '@chakra-ui/react';
import { Search, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useAccessCheck } from '@/hooks/useAccess';
import { useThemeColors } from '@/hooks/useThemeColors';
import {
    formatYmd,
    parseEnumParam,
    parseYmd,
    serializeOmitDefault,
    useUrlListState,
} from '@/hooks/useUrlListState';

const MaintenanceHistoryPage: React.FC = () => {
    const routeParams = useParams<{ id?: string; truckId?: string }>();
    const truckId = routeParams.truckId ?? routeParams.id;
    const [searchParams] = useSearchParams();
    const truckPlate = searchParams.get('plate_number') ?? '';
    const navigate = useNavigate();
    const access = useAccessCheck();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const { values, setValues } = useUrlListState({
        defaults: {
            filter: 'weekly' as 'weekly' | 'monthly',
            date: formatYmd(new Date()),
            search: '',
        },
        parse: {
            filter: (raw, d) =>
                parseEnumParam(raw, ['weekly', 'monthly'] as const, d.filter),
            date: (raw, d) => {
                const parsed = parseYmd(raw);
                return parsed ? formatYmd(parsed) : d.date;
            },
            search: (raw, d) => raw ?? d.search,
        },
        serialize: {
            filter: (v, d) => serializeOmitDefault(v, d.filter),
            date: (v, d) => serializeOmitDefault(v, d.date),
            search: (v, d) => serializeOmitDefault(v, d.search),
        },
    });
    const filterType = values.filter;
    const currentDate = useMemo(
        () => parseYmd(values.date) ?? new Date(),
        [values.date],
    );
    const searchQuery = values.search;

    // --- 🌟 NAVIGATION LOGIC ---
    const adjustDate = (direction: number) => {
        const newDate = new Date(currentDate);
        if (filterType === 'weekly') {
            newDate.setDate(newDate.getDate() + (direction * 7));
        } else {
            newDate.setMonth(newDate.getMonth() + direction);
        }
        setValues({ date: formatYmd(newDate) });
    };

    const formatDateUTC = (d: Date) => d.toISOString().slice(0, 10); // YYYY-MM-DD (UTC)

    const formatDateDisplay = (isoDate: string, opts: Intl.DateTimeFormatOptions) => {
        // Use noon UTC to avoid edge-case midnight shifts in display.
        const d = new Date(`${isoDate}T12:00:00.000Z`);
        return d.toLocaleDateString('en-US', opts);
    };

    const dateRange = useMemo(() => {
        const baseIso = currentDate.toISOString().slice(0, 10); // match server date-only meaning
        const base = new Date(`${baseIso}T00:00:00.000Z`);

        if (filterType === 'weekly') {
            const day = base.getUTCDay(); // 0 (Sun) - 6 (Sat)
            const diff = base.getUTCDate() - day + (day === 0 ? -6 : 1); // shift to Monday

            const start = new Date(base);
            start.setUTCDate(diff);

            const end = new Date(start);
            end.setUTCDate(start.getUTCDate() + 6);

            return { start, end, from: formatDateUTC(start), to: formatDateUTC(end) };
        }

        // monthly
        const start = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), 1));
        const end = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0));

        return { start, end, from: formatDateUTC(start), to: formatDateUTC(end) };
    }, [currentDate, filterType]);

    const dateRangeDisplay = useMemo(() => {
        if (filterType === 'weekly') {
            return `${formatDateDisplay(dateRange.from, { month: 'short', day: 'numeric' })} - ${formatDateDisplay(dateRange.to, { month: 'short', day: 'numeric', year: 'numeric' })}`;
        }
        return formatDateDisplay(dateRange.from, { month: 'long', year: 'numeric' });
    }, [dateRange, filterType]);

    const permissions = useMemo(() => ({
        can_edit: false,
        can_create: access.can_create,
        can_delete: access.can_delete,
        can_view: access.can_view,
    }), [access]);

    const tableParams = useMemo(() => {
        const params: Record<string, any> = {
            search: searchQuery,
            transaction_date_from: dateRange.from,
            transaction_date_to: dateRange.to,
        };

        if (truckPlate) {
            params.plate_number = truckPlate;
        }

        return params;
    }, [searchQuery, truckPlate, dateRange]);

    // NOTE: Truck plate number is passed from `Trucks` via ?plate_number=...

    const handleSearch = () => {
        setValues({ search: searchRef.current?.value || '' });
        tableRef.current?.applyFilter('search', searchRef.current?.value || '');
    };

    const tableOptions: DataTableOptions = useMemo(() => ({
        dataSource: `/budget/parts-expense`,
        primaryKey: 'id',
        showSelection: false,
        showCheckboxes: false,
        softDelete: false,
        displayInModal: true,
        showActionsColumn: false,
        noRecordText: 'No maintenance records found',
        dataFields: {
            receipt_no: { name: 'Receipt', withSort: true, maxWidth: '150px' },
            article: { name: 'Article', withSort: true },
            quantity: { name: 'Qty', withSort: true, maxWidth: '80px' },
            amount_per_item: { name: 'Price', withSort: true, maxWidth: '120px' },
            total_amount: { name: 'Total Cost', withSort: false, maxWidth: '150px' },
            shift: { name: 'Shift', withSort: true, maxWidth: '100px' },
            transaction_date: { name: 'Date', withSort: true, maxWidth: '200px' },
        },
        actions: [],
    }), [truckId]);

    const customRenderers = useMemo(() => ({
        total_amount: (_: any, row: any) => {
            const total = Number(row.quantity) * Number(row.amount_per_item);
            return `P ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
    }), []);

    return (
        <AdminLayout>
            <Box p={4} h="100%" display="flex" flexDirection="column">
                <VStack spacing={2} align="stretch" flex="1" minH={0}>
                    <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
                        
                        <CardHeader py={3} px={5}>
                            <HStack spacing={3} align="center">
                                <IconButton aria-label="Back" icon={<Icon as={ChevronLeft} boxSize={6} />} variant="ghost" size="sm" onClick={() => navigate('/fleet-management/trucks')} />
                                <Box><Heading size="md" mb={1}>Maintenance History: {truckPlate}</Heading></Box>
                            </HStack>
                        </CardHeader>
                        
                        {/* 🌟 INTEGRATED FILTER BAR */}
                        <CardHeader py={0} px={5} flexShrink={0}>
                            <HStack justify="space-between" align="center" w="100%" pb={4}>
                                <HStack spacing={2}>
                                    <Select size="sm" w="120px" value={filterType} onChange={(e) => setValues({ filter: e.target.value as 'weekly' | 'monthly' })}>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                    </Select>
                                    
                                    <HStack spacing={1}>
                                        <IconButton icon={<ChevronLeft size={18} />} aria-label="Prev" size="sm" variant="ghost" onClick={() => adjustDate(-1)} />
                                        
                                        <HStack 
                                            border="1px solid" borderColor="gray.200" px={3} py={1} borderRadius="md" bg="white" cursor="pointer"
                                            onClick={() => (document.getElementById('date-picker') as HTMLInputElement)?.showPicker()}
                                        >
                                            <Icon as={Calendar} size={14} color="gray.500" />
                                            <Text fontWeight="medium" fontSize="sm" color="gray.600">{dateRangeDisplay}</Text>
                                            <input 
                                                id="date-picker" type="date" style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
                                                onChange={(e) => e.target.value && setValues({ date: e.target.value })}
                                            />
                                        </HStack>

                                        <IconButton icon={<ChevronRight size={18} />} aria-label="Next" size="sm" variant="ghost" onClick={() => adjustDate(1)} />
                                    </HStack>
                                </HStack>

                            </HStack>
                         </CardHeader>

                         <CardHeader py={0} px={5} flexShrink={0}>
                             <Box maxW="400px" pb={4}>
                                 <InputGroup size="sm">
                                     <Input ref={searchRef} placeholder="Search records..." onKeyDown={(e) => e.key === 'Enter' && handleSearch()} key={searchQuery} defaultValue={searchQuery} />
                                     <InputRightElement><IconButton aria-label="Search" icon={<Search size={16} />} size="sm" variant="ghost" onClick={handleSearch} /></InputRightElement>
                                 </InputGroup>
                             </Box>
                         </CardHeader>
                       
                         <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column">
                        {truckPlate ? (
                            <DataTable
                                ref={tableRef}
                                options={tableOptions}
                                params={tableParams}
                                access={permissions}
                                customRenderers={customRenderers}
                            />
                        ) : (
                            <Box py={10} textAlign="center">
                                <Text color="gray.500">No plate number found...</Text>
                            </Box>
                        )}
                        </CardBody>
                    </Card>
                </VStack> 
            </Box> 
        </AdminLayout>
    );
};

export default MaintenanceHistoryPage;
